import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./WellSyncPatientValidationModal.module.scss";

const REQUIRED_IDS = [
  "firstName",
  "middleName",
  "lastName",
  "phoneNumber",
  "emailAddress",
  "dobInput",
  "genderSelect",
  "sexAtBirthSelect",
  "addrLine1",
  "addrCity",
  "addrState",
  "addrZip",
];

function mapGenderFromProfile(gender) {
  if (gender === 0 || gender === "0" || gender === "Male") return "Male";
  if (gender === 1 || gender === "1" || gender === "Female") return "Female";
  if (typeof gender === "string" && gender.trim()) return gender.trim();
  return "";
}

function formatDobForDisplay(dob) {
  if (!dob) return "";
  const s = String(dob);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const d = new Date(s.includes("T") ? s : `${s}T00:00:00`);
  if (isNaN(d.getTime())) return s;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function dobToIso(dobDisplay) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((dobDisplay || "").trim());
  if (!m) return "";
  return `${m[3]}-${m[1]}-${m[2]}T00:00:00Z`;
}

function buildInitialForm(patient) {
  const gender = mapGenderFromProfile(patient?.gender);
  let sexAtBirth = patient?.sexAtBirth || patient?.sex_at_birth || "";
  if (!sexAtBirth && (gender === "Male" || gender === "Female")) {
    sexAtBirth = gender;
  }
  return {
    firstName: patient?.firstName || "",
    middleName: patient?.middleName || patient?.middlename || "",
    lastName: patient?.lastName || "",
    phoneNumber: patient?.phone || "",
    emailAddress: patient?.email || "",
    dobInput: formatDobForDisplay(patient?.dob),
    genderSelect: gender,
    sexAtBirthSelect: sexAtBirth,
    addrLine1: patient?.address || patient?.addressLine1 || "",
    addrLine2: patient?.addressLine2 || patient?.address_line_2 || "",
    addrCity: patient?.city || "",
    addrState: (patient?.state || "").slice(0, 2).toUpperCase(),
    addrZip: patient?.zip || "",
    ssnInput: patient?.ssn || "",
  };
}

export function WellSyncPatientValidationModal({
  open,
  patient,
  onCancel,
  onVerified,
}) {
  const [form, setForm] = useState(() => buildInitialForm(patient));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(patient));
      setErrors({});
    }
  }, [open, patient]);

  const setField = useCallback((id, value) => {
    setForm((f) => ({ ...f, [id]: value }));
    setErrors((e) => {
      const next = { ...e };
      delete next[id];
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const next = {};
    REQUIRED_IDS.forEach((id) => {
      const v = String(form[id] != null ? form[id] : "").trim();
      if (!v) next[id] = "Required.";
    });
    const dob = form.dobInput.trim();
    if (dob && !/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
      next.dobInput = "Use mm/dd/yyyy format.";
    }
    const email = form.emailAddress.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.emailAddress = "Enter a valid email address.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [form]);

  const handleContinue = useCallback(() => {
    if (!validate()) return;
    const fn = form.firstName.trim();
    const mn = form.middleName.trim();
    const ln = form.lastName.trim();
    const fullName = `${fn}${mn ? ` ${mn}` : ""} ${ln}`
      .replace(/\s+/g, " ")
      .trim();
    onVerified({
      firstName: fn,
      middleName: mn,
      lastName: ln,
      fullName,
      phone: form.phoneNumber.trim(),
      email: form.emailAddress.trim(),
      dob: dobToIso(form.dobInput),
      dobDisplay: form.dobInput.trim(),
      gender: form.genderSelect,
      sexAtBirth: form.sexAtBirthSelect,
      address: form.addrLine1.trim(),
      addressLine2: form.addrLine2.trim(),
      city: form.addrCity.trim(),
      state: form.addrState.trim().toUpperCase(),
      zip: form.addrZip.trim(),
      ssn: form.ssnInput.trim() || undefined,
    });
  }, [form, onVerified, validate]);

  const modalBody = useMemo(
    () => (
      <>
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Identity</div>
          <div className={styles.row3}>
            <Field
              id="firstName"
              label="First Name"
              required
              value={form.firstName}
              error={errors.firstName}
              onChange={setField}
            />
            <Field
              id="middleName"
              label="Middle Name"
              required
              value={form.middleName}
              error={errors.middleName}
              onChange={setField}
            />
            <Field
              id="lastName"
              label="Last Name"
              required
              value={form.lastName}
              error={errors.lastName}
              onChange={setField}
            />
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Contact</div>
          <div className={styles.row2}>
            <Field
              id="phoneNumber"
              label="Phone Number"
              required
              value={form.phoneNumber}
              error={errors.phoneNumber}
              onChange={setField}
            />
            <Field
              id="emailAddress"
              label="Email"
              required
              type="email"
              value={form.emailAddress}
              error={errors.emailAddress}
              onChange={setField}
            />
          </div>
          <Field
            id="dobInput"
            label="Date of Birth"
            required
            value={form.dobInput}
            placeholder="mm/dd/yyyy"
            error={errors.dobInput}
            onChange={setField}
          />
          <div className={styles.row2}>
            <SelectField
              id="genderSelect"
              label="Gender"
              required
              value={form.genderSelect}
              error={errors.genderSelect}
              onChange={setField}
              options={[
                { value: "", label: "Select…" },
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
                { value: "Non-Binary", label: "Non-Binary" },
              ]}
            />
            <SelectField
              id="sexAtBirthSelect"
              label="Sex at Birth"
              required
              value={form.sexAtBirthSelect}
              error={errors.sexAtBirthSelect}
              onChange={setField}
              options={[
                { value: "", label: "Select…" },
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]}
            />
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Address</div>
          <Field
            id="addrLine1"
            label="Address Line 1"
            required
            value={form.addrLine1}
            error={errors.addrLine1}
            onChange={setField}
          />
          <Field
            id="addrLine2"
            label="Address Line 2"
            optional
            value={form.addrLine2}
            onChange={setField}
          />
          <div className={styles.rowCity}>
            <Field
              id="addrCity"
              label="City"
              required
              value={form.addrCity}
              error={errors.addrCity}
              onChange={setField}
            />
            <Field
              id="addrState"
              label="State"
              required
              maxLength={2}
              value={form.addrState}
              error={errors.addrState}
              onChange={setField}
            />
            <Field
              id="addrZip"
              label="ZIP"
              required
              value={form.addrZip}
              error={errors.addrZip}
              onChange={setField}
            />
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.sectionLabel}>
            Identification <span className={styles.optional}>(optional)</span>
          </div>
          <Field
            id="ssnInput"
            label="SSN"
            optional
            placeholder="XXX-XX-XXXX or last 4 digits"
            value={form.ssnInput}
            onChange={setField}
          />
        </div>
      </>
    ),
    [form, errors, setField],
  );

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Confirm Patient Details</h2>
            <p className={styles.subtitle}>Required before prescribing</p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onCancel}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.body}>{modalBody}</div>
        <div className={styles.footer}>
          <button type="button" className={styles.btnCancel} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleContinue}
          >
            Verify &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  required,
  optional,
  value,
  error,
  onChange,
  type,
  placeholder,
  maxLength,
}) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? <span className={styles.req}>*</span> : null}
        {optional ? (
          <span className={styles.optionalInline}> (optional)</span>
        ) : null}
      </label>
      <input
        id={id}
        className={styles.input}
        type={type || "text"}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(id, e.target.value)}
      />
      {error ? <div className={styles.error}>{error}</div> : null}
    </div>
  );
}

function SelectField({ id, label, required, value, error, onChange, options }) {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? <span className={styles.req}>*</span> : null}
      </label>
      <select
        id={id}
        className={styles.input}
        value={value}
        onChange={(e) => onChange(id, e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value || "empty"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <div className={styles.error}>{error}</div> : null}
    </div>
  );
}

export default WellSyncPatientValidationModal;
